<template>
    <section
      :class="[
        'wrapper',
        'content',
        'content--centered',
        $arMediaQuery.pageContent.maxWidth('sm') && 'sm-max',
        $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
      ]">
      <am2-step-link
        class="step-back-link"
        text="Back to integrations"
        @click="handleBackLinkClick" />
      <am2-ticket-tailor-modal
        :is-show="showTicketTailorCredentialsModal"
        :loading="isCreatingTicketTailorIntegration"
        @confirm="handleTicketTailorCredentialsConfirm"
        @cancel="showTicketTailorCredentialsModal = false"
      />

      <div
        :class="[
          'title-section',
          $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
        ]">
        <div class="title-section-left">
          <am2-icon-title-section
            title="TicketTailor"
            description="Automatically sync your data from TicketTailor"
            :icon-props="{
              name: 'ticket-tailor',
              color: $arStyle.color.tryBooking,
              height: '46px',
              width: '40px',
            }"
          />
        </div>
        <div class="title-section-right" v-if="!isFetchingTicketTailorIntegration && integrationsSummary">
          <am2-integration-account-select
            v-if="!isFetchingTicketTailorIntegration && ticketTailorIntegration && currentIntegration"
            :default-selected-integration-oid="currentIntegration.oid"
            :available-integrations="integrationsSummary"
            @select="handleIntegrationChange"
            @new-account="handleAddNewIntegration"
            :style="{ marginRight: '10px', maxWidth: 'calc(100vw - 80px)', }"
          />
          <am2-expand-button-dropdown
            align="left"
            :button-props="{ iconName: 'settings' }"
            :items="[
              {
                name: 'Reconnect Integration',
                value: 'reconnectIntegration',
              },
              {
                name: 'Remove Account',
                value: 'removeIntegration',
              },
            ]"
            @select="handleSettingOptionSelect" />
        </div>
      </div>

      <am2-tabs
        :items="[
          { title: 'Notifications' },
          { title: 'Status' }
        ]"
        :selected-tab-index="selectedTabIndex"
        class="tab-options"
        @select="(selected, idx) => selectedTabIndex = idx"
      />
      <div
        v-if="selectedTabIndex === 0"
      >
        <NotificationsContainer
          :integration="currentIntegration"
          :loading="isFetchingTicketTailorIntegration"
        />
      </div>
      <div
        v-if="selectedTabIndex === 1"
        class="integration-status-container"
      >
      <section v-if="isCreatingTicketTailorIntegration || isFetchingTicketTailorIntegration" class="integration-loading-container">
        <am2-loading-bubble />
      </section>
      <section v-else>
        <section v-if="integrationFailed" class="integration-failed-message">
          <ar-snackbar
            type="warning"
            message="Your TicketTailor connection is no longer valid. This means we can't sync your member data. <a>Please reconnect your account</a>"
            @anchorClick="handleReconnectIntegration"
            :style="{ width: '100%' }"
          />
        </section>

        <SyncBlockContainer
          v-else
          class="sync-block-container"
          :is-fetching-sync-task="isFetchingSyncTask"
          :sync-status="syncStatus"
          :sync-time="syncTime"
          :sync-copies="{
            'in-progress': 'Syncing data from TicketTailor',
            'pending': 'The data sync is still pending',
            'completed': 'Your TicketTailor account is currently connected',
            'cancelled': 'Your TicketTailor account connection is cancelled',
            'failed': this.syncErrorMessage,
            'default': 'Syncing tasks not found',
          }"
          @startSync="handleStartSync"
          @stopSync="handleStopSync"
        />
      </section>
      </div>

    </section>
  </template>

  <script>
  import { mapState, mapActions, mapMutations } from 'vuex';
  import SyncBlockContainer from '../components/sync-block-container';
  import NotificationsContainer from '../components/notifications-container';

  export default {
    name: 'TicketTailorIntegration',
    layout: 'default',
    components: {
      SyncBlockContainer,
      NotificationsContainer
    },

    data: () => ({
      currentlySelectedIntegrationIndex: 0,
      syncStatusPoller: null,
      showTicketTailorCredentialsModal: false,
      selectedTabIndex: 1
    }),

    computed: {
      ...mapState({
        ticketTailorIntegration: state => state.ticketTailorIntegration.integration,
        isFetchingTicketTailorIntegration: state => state.ticketTailorIntegration.isFetchingIntegration,
        isAddingTicketTailorIntegration: state => state.ticketTailorIntegration.isAddingIntegration,
        isCreatingTicketTailorIntegration: state => state.ticketTailorIntegration.isCreatingIntegration,
        isDeletingTicketTailorIntegration: state => state.ticketTailorIntegration.isDeletingIntegration,
        isFetchingSyncTask: state => state.ticketTailorIntegration.isFetchingSyncTask,
        syncTask: state => state.ticketTailorIntegration.syncTask,
        syncStatus: state => state.ticketTailorIntegration.syncStatus,
      }),

      ticketTailorIntegrationEnabled() {
        return true;
      },

      syncErrorMessage() {
        return this.syncTask?.statusDetails?.lastError || this.syncTask?.statusDetails?.reason;
      },

      syncTime() {
        return this.syncTask?.sysMtime || null;
      },

      integrationsSummary() {
        if (!this.ticketTailorIntegration) return null;
        const summary = this.ticketTailorIntegration
          .map(i => ({
            oid: i.oid,
            name: i.accountId,
            email: i.accountId
          }));

        return summary
      },

      currentIntegration() {
        if (this.currentlySelectedIntegrationIndex === null) return null;
        if (!this.ticketTailorIntegration || this.ticketTailorIntegration.length < 1 || !this.ticketTailorIntegration[this.currentlySelectedIntegrationIndex]) return null;
        return this.ticketTailorIntegration[this.currentlySelectedIntegrationIndex];
      },

      integrationFailed() {
        if (!this.currentIntegration) return false;
        return this.currentIntegration.status === 'failed';
      },
    },

    async mounted() {
      if (!this.ticketTailorIntegrationEnabled) {
        this.handleBackLinkClick();
      }
      await this.fetchIntegration();
      this.startFetchTasksPolling();
    },

    beforeDestroy() {
      this['ticketTailorIntegration/RESET_INTEGRATION']();

      if (this.syncStatusPoller) {
        clearInterval(this.syncStatusPoller);
      }
    },

    methods: {
      ...mapActions([
        'SHOW_CONFIRM',
        'ticketTailorIntegration/FETCH_INTEGRATION',
        'ticketTailorIntegration/DELETE_INTEGRATION',
        'ticketTailorIntegration/SYNC_START',
        'ticketTailorIntegration/SYNC_STOP',
        'ticketTailorIntegration/FETCH_SYNC_TASK',
        'ticketTailorIntegration/CREATE_TICKET_TAILOR_INTEGRATION',
        'promoterTasks/START_POLLING_PENDING_TASKS',
      ]),
      ...mapMutations([
        'ticketTailorIntegration/RESET_INTEGRATION',
      ]),
      startFetchTasksPolling() {
        if (this.currentIntegration?.oid && !this.isFetchingSyncTask) {
          this.fetchTasks();
          this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
        }
      },
      async fetchIntegration() {
        await this['ticketTailorIntegration/FETCH_INTEGRATION']();
      },

      async fetchTasks() {
        await this['ticketTailorIntegration/FETCH_SYNC_TASK'](this.currentIntegration.oid);

        if (this.currentIntegration && this.hasExistingTask && this.syncStatus !== "in-progress") {
          window.clearInterval(this.syncStatusPoller);
          this.syncStatusPoller = null;
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }
      },

      async handleTicketTailorCredentialsConfirm({ apiKey, accountName }) {
        const succeed = await this['ticketTailorIntegration/CREATE_TICKET_TAILOR_INTEGRATION']({
            apiKey, accountName
        });
        if (succeed) {
          this.fetchIntegration();

          this.showTicketTailorCredentialsModal = false;
        }
      },


      hasExistingTask() {
        return this.syncTask !== null;
      },

      handleBackLinkClick() {
        this.$router.push({ path: '/settings/integrations' });
      },

      handleIntegrationChange(integration, index) {
        this.currentlySelectedIntegrationIndex = index;
        this.fetchTasks();
      },

      async handleAddNewIntegration() {
        this.showTicketTailorCredentialsModal = true;
      },

      async handleStartSync() {
        setTimeout(() => {
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }, 750);
        await this['ticketTailorIntegration/SYNC_START']({ oid: this.currentIntegration.oid });
        if (this.syncStatus !== 'completed') {
          this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
        }
      },

      handleStopSync() {
        this['ticketTailorIntegration/SYNC_STOP']({oid: this.currentIntegration.oid});
        setTimeout(() => {
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }, 750);
      },

      async handleReconnectIntegration() {
        const agreed = await this.SHOW_CONFIRM({
          messageHtml: `Make sure you are logging in with the correct TicketTailor account before reconnecting it.`,
          confirmButtonText: 'Reconnect account',
        });
        if (!agreed) {
          return;
        }

        const succeed = await this['ticketTailorIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
        if (!succeed) {
          return;1
        }

        this.handleAddNewIntegration();
      },

      async handleSettingOptionSelect(item) {
        if (item.value === 'removeIntegration') {
          const answer = await this.SHOW_CONFIRM({
            messageHtml: 'Removing the TicketTailor integration will stop syncing your data from TicketTailor.',
            confirmButtonText: 'Remove integration',
            iconName: 'alert-question-mark',
            title: 'Are you sure?',
          });
          if (answer) {
            try {
              await this['ticketTailorIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
              if (this.ticketTailorIntegration.length > 0) {
                this.currentlySelectedIntegrationIndex = 0;
                await this.fetchIntegration();
                this.startFetchTasksPolling();
              } else {
                this.$router.push({ path: '/settings/integrations' });
              }
            } catch (e) {
              this.$arNotification.push({ type: 'error', message: 'Failed to remove integration' });
            }
          }
        } else if (item.value === 'reconnectIntegration') {
          this.handleReconnectIntegration();
        } else if (item.value == 'openSettingsModal') {
          if (!this.currentIntegration) return;
        }
      },
    }
  };
  </script>

  <style lang="scss" scoped>
  .wrapper {

    .tab-options {
      margin: 60px 0 20px 0;
      border-bottom: 1px $skyBlueGrey500 solid;
    }

    &.sm-max {
      padding: 0 24px;
    }
    &.xs-max {
      padding: 0 12px;
    }

    .integration-loading-container {
      display: flex;
      justify-content: center;
      margin-top: 65px;
    }


    .step-back-link {
      margin-top: 48px;
    }

    .title-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 48px;

      .title-section-left {
        display: inline-flex;
        align-items: flex-start;

        .tag {
          position: relative;
          margin-left: 10px;
          top: 4px;
        }
      }

      .title-section-right {
        display: inline-flex;
        align-items: center;

        .select {
          width: 300px;
          margin-right: 10px;
        }
      }

      &.md-max {
        flex-direction: column;
        align-items: flex-start;

        .title-section-right {
          margin-top:16px;
        }
      }
    }


    .sync-block-container {
      margin-top: 0px;
    }
  }
  </style>
